import React, { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Tune } from '@mui/icons-material';
import { DsButton } from '../design-system/components';
import './DesignHarnessCalendarPreview.css';

type SampleSlot = { time: string; recruitment: number; confirmed: number };
const weekdays = ['日', '月', '火', '水', '木', '金', '土'];
const pad = (value: number) => String(value).padStart(2, '0');
const dateKey = (year: number, month: number, day: number) => `${year}-${pad(month)}-${pad(day)}`;
const sampleHoliday = (year: number, month: number, day: number) => year === 2026 && month === 11 ? ({ 3: '文化の日', 23: '勤労感謝の日' } as Record<number, string>)[day] : undefined;

const makeSlots = (day: number, weekday: number): SampleSlot[] => {
  if (weekday === 0 || day % 7 === 0) return [];
  const count = weekday === 6 ? 2 : day % 5 === 0 ? 4 : 3;
  return Array.from({ length: count }, (_, index) => ({
    time: `${pad(7 + index)}:00`,
    recruitment: 2 + (day + index) % 3,
    confirmed: (day + index) % 3,
  }));
};

const DesignHarnessCalendarPreview: React.FC = () => {
  const [monthDate, setMonthDate] = useState(() => new Date(2026, 10, 1));
  const [selectedDay, setSelectedDay] = useState<number | null>(4);
  const [overrides, setOverrides] = useState<Record<string, SampleSlot[]>>({});
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth() + 1;
  const daysInMonth = new Date(year, month, 0).getDate();
  const firstWeekday = new Date(year, month - 1, 1).getDay();
  const cells = useMemo(() => {
    const total = Math.ceil((firstWeekday + daysInMonth) / 7) * 7;
    return Array.from({ length: total }, (_, index) => {
      const day = index - firstWeekday + 1;
      return day >= 1 && day <= daysInMonth ? day : null;
    });
  }, [firstWeekday, daysInMonth]);
  const slotsFor = (day: number) => overrides[dateKey(year, month, day)] ?? makeSlots(day, new Date(year, month - 1, day).getDay());
  const selectedSlots = selectedDay === null ? [] : slotsFor(selectedDay);

  const moveMonth = (offset: number) => {
    setMonthDate(previous => new Date(previous.getFullYear(), previous.getMonth() + offset, 1));
    setSelectedDay(null);
  };
  const updateRecruitment = (index: number, offset: number) => {
    if (selectedDay === null) return;
    const key = dateKey(year, month, selectedDay);
    setOverrides(previous => ({
      ...previous,
      [key]: (previous[key] ?? makeSlots(selectedDay, new Date(year, month - 1, selectedDay).getDay())).map((slot, slotIndex) =>
        slotIndex === index ? { ...slot, recruitment: Math.max(slot.confirmed, slot.recruitment + offset) } : slot
      ),
    }));
  };

  return <div className="harness-calendar-page">
    <div className="harness-calendar-heading">
      <div><h1>月次シフト枠</h1><p>月表示・日別の枠・詳細操作を確認するためのカレンダーパターン</p></div>
      <span className="harness-calendar-preview-tag">サンプルデータ</span>
    </div>
    <div className="harness-calendar-layout">
      <section className="harness-calendar-card" aria-label="月次シフト枠カレンダー">
        <div className="harness-calendar-toolbar">
          <div><h2>{year}年{pad(month)}月</h2><p>日付を選ぶと時間帯ごとの枠を確認できます</p></div>
          <div className="harness-calendar-month-actions">
            <button type="button" aria-label="前の月" onClick={() => moveMonth(-1)}><ChevronLeft /></button>
            <button type="button" aria-label="次の月" onClick={() => moveMonth(1)}><ChevronRight /></button>
          </div>
        </div>
        <div className="harness-calendar-grid" aria-label={`${year}年${month}月`}>
          {weekdays.map((weekday, index) => <div className={`harness-calendar-weekday ${index === 0 ? 'is-sunday' : index === 6 ? 'is-saturday' : ''}`} key={weekday}>{weekday}</div>)}
          {cells.map((day, index) => {
            if (day === null) return <div className="harness-calendar-empty" key={`empty-${index}`} />;
            const weekday = index % 7;
            const slots = slotsFor(day);
            const selected = day === selectedDay;
            const holiday = sampleHoliday(year, month, day);
            return <button
              type="button"
              className={`harness-calendar-day ${weekday === 0 ? 'is-sunday' : weekday === 6 ? 'is-saturday' : ''} ${holiday ? 'is-holiday' : ''} ${selected ? 'is-selected' : ''}`}
              aria-label={`${month}月${day}日${holiday ? `、${holiday}` : ''}、${slots.length}時間帯、募集${slots.reduce((sum, slot) => sum + slot.recruitment, 0)}枠`}
              aria-pressed={selected}
              key={day}
              onClick={() => setSelectedDay(day)}
            >
              <span className="harness-calendar-date">{day}</span>
              {holiday && <span className="harness-calendar-holiday">{holiday}</span>}
              <span className="harness-calendar-day-slots">
                {slots.slice(0, 3).map(slot => <span className="harness-calendar-slot" key={slot.time}><span>{slot.time}〜</span><span>募{slot.recruitment} / 確{slot.confirmed}</span></span>)}
                {slots.length > 3 && <span className="harness-calendar-more">+{slots.length - 3} もっと見る</span>}
                {slots.length === 0 && <span className="harness-calendar-no-slots">枠なし</span>}
              </span>
              <span className="harness-calendar-mobile-count">{slots.length > 0 ? `${slots.length}時間帯` : '枠なし'}</span>
            </button>;
          })}
        </div>
      </section>
      <aside className="harness-calendar-detail" aria-label="選択した日のシフト枠">
        {selectedDay === null ? <div className="harness-calendar-detail-empty"><strong>日付を選択</strong><p>カレンダーの日付を押すと、時間帯ごとの募集枠と確定枠が表示されます。</p></div> : <>
          <div className="harness-calendar-detail-heading"><span>日別の詳細</span><h2>{year}年{month}月{selectedDay}日 <small>（{weekdays[new Date(year, month - 1, selectedDay).getDay()]}）</small></h2><p>募集枠数はこのプレビュー内だけで変更できます。</p></div>
          {selectedSlots.length === 0 ? <p className="harness-calendar-detail-none">この日のシフト枠はありません。</p> : <div className="harness-calendar-detail-slots">{selectedSlots.map((slot, index) => <div className="harness-calendar-detail-slot" key={slot.time}><strong>{slot.time}〜{pad(Number(slot.time.slice(0, 2)) + 1)}:00</strong><div><span>確定 {slot.confirmed}</span><span className="harness-calendar-stepper"><button type="button" aria-label={`${slot.time}の募集枠を減らす`} disabled={slot.recruitment <= slot.confirmed} onClick={() => updateRecruitment(index, -1)}>−</button><span>募集 {slot.recruitment}</span><button type="button" aria-label={`${slot.time}の募集枠を増やす`} onClick={() => updateRecruitment(index, 1)}>＋</button></span></div></div>)}</div>}
          <div className="harness-calendar-detail-footer"><DsButton type="button" onClick={() => setSelectedDay(null)}>閉じる</DsButton></div>
        </>}
      </aside>
    </div>
    <p className="harness-calendar-note"><Tune fontSize="small" />元画面の7列カレンダーと日別枠を参考にしたデザイン確認用です。実際の医師シフトには接続していません。</p>
  </div>;
};

export default DesignHarnessCalendarPreview;
